import React from 'react';
import { useLocation, useParams } from 'react-router';
import { TalentBios, useApi } from '@axiom/ui';
import type { Candidate } from '@axiom/validation';

import { CandidateApi } from '../../api/candidate-api';
import { CandidateLegalTechSkillsApi } from '../../api/candidate-legal-tech-skills-api';
import { EnvUtil } from '../../utils/env-util';
import { PublicRefIdSlugUtil } from '../../utils/public-ref-id-slug-util';
import { TaxonomyApi } from '../../api/taxonomy-api';

export type CandidateBiosProps = {
  candidateId?: Candidate['id'];
  isDownloading?: boolean;
  name?: string;
  onSave?: () => void;
};

const noopFn = () => {};

export const CandidateBios = ({
  candidateId,
  isDownloading = false,
  name,
  onSave = noopFn,
}: CandidateBiosProps) => {
  const urlParams = useParams();
  const location = useLocation();

  const { candidateId: urlCandidateId } = urlParams;
  const isAnon = location.pathname.includes('/anonymous');
  const idOrPublicRefId = PublicRefIdSlugUtil.sanitizeId(urlCandidateId);
  const shouldCallLimitedCandidates = !candidateId || undefined;
  const [limitedCandidate] = useApi(
    shouldCallLimitedCandidates && idOrPublicRefId
      ? CandidateApi.readLimitedCandidate(idOrPublicRefId)
      : undefined
  );

  const foundCandidateId = candidateId ?? limitedCandidate?.data.id;
  if (!foundCandidateId) {
    throw new Error('No candidate id provided');
  }

  const [{ data: candidate }, { data: taxonomy }] = useApi(
    CandidateApi.readCandidate(foundCandidateId, {
      isAnonymous: isAnon,
    }),
    TaxonomyApi.readTaxonomy()
  );
  const [
    { data: groupedExperiences } = { data: [] },
    { data: legalTechSkills } = { data: [] },
  ] = useApi(
    candidate.id
      ? CandidateApi.readCandidateGroupedExperiences(candidate.id)
      : undefined,
    candidate.id
      ? CandidateLegalTechSkillsApi.readLegalTechSkills(candidate.id)
      : undefined
  );

  const baseUrl = `/pdf/${candidate.id}`;

  const handleOnNavigate = () => {
    const href = `${baseUrl}${isAnon ? '/anonymous' : ''}`;

    window.open(href, 'download_bio');
  };

  return (
    <TalentBios
      name={name}
      onSave={onSave}
      candidate={candidate}
      clientUrl={EnvUtil.clientEngagementsUrl}
      groupedExperiences={groupedExperiences}
      legalTechSkills={legalTechSkills}
      taxonomy={taxonomy}
      onNavigate={handleOnNavigate}
      isDownloading={isDownloading}
      isAnonymous={isAnon}
    />
  );
};
